import React from 'react'

import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, type AddressCell, Alert } from '@/components/ui'
import { useApi, useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam } from '@/insights/common/dataSourceParam'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptions } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { celsiusToUser } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'

interface ReeferOperationHistoryParams extends InsightParametersType {
  companyId?: number
  assetId?: string
}

interface ReeferOperationHistory {
  id: number
  companyId: number
  latitude: number
  longitude: number
  umdId: number
  reeferState: string
  mode: string
  source: string
  engineSpeed: string
  batteryVolts: string
  location: string
  operationalMode: string
  zone1Mode: string
  zone1SP: number
  zone1SA: number
  zone1RA: number
  zone2Mode: string
  zone2SP: number
  zone2SA: number
  zone2RA: number
  sampleTime: Date
  ambientTemperature: number
  assetId: number
  assetName: string
}

const formatReeferTemperature = (reeferState: string, temperature: number, units: 'Imperial' | 'Metric'): string => {
  if (reeferState !== 'On') return ''

  return celsiusToUser(temperature, 1, units)
}

const ReeferOperationHistory: InsightDefinition<ReeferOperationHistoryParams, ReeferOperationHistory[]> = {
  active: true,
  slug: 'asset/reefer-operation-history',
  category: 'asset',
  name: 'Reefer Operation History',
  description: 'List detailed reefer operational history',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptions.map((d) => (d.value === 'today' ? { ...d, isDefault: true } : d)),
      },
    ],

    isValid: (configuration: ReeferOperationHistoryParams) => {
      if (configuration.companyId && configuration.assetId) {
        return true
      }
      return 'Company and Asset are required.'
    },
  },

  load: async (api: ApiService, parameters: ReeferOperationHistoryParams) => {
    const path = `/insights/asset/reefer-operation-history?${buildQuery(parameters)}`
    const response = await api.get<ReeferOperationHistory[]>({
      path,
      origin: 'ReeferOperationHistory',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferOperationHistoryParams, title: string) => {
    const path = '/insights/asset/reefer-operation-history'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={16} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No history found".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const parameters = configuration.parameters as ReeferOperationHistoryParams
    type HeaderType = { key: string; title: string; size: number } | string
    const headers: HeaderType[] = [{ key: 'Date', title: 'Date', size: 200 }]
    let hasZone1 = false,
      hasZone2 = false
    data.forEach((sample) => {
      if (sample.zone1Mode || sample.zone1RA || sample.zone1SA) {
        hasZone1 = true
      }
      if (sample.zone2Mode || sample.zone2RA || sample.zone2SA) {
        hasZone2 = true
      }
    })
    headers.push('Reefer State')
    headers.push('Mode')
    headers.push('Source')
    headers.push('Operational Mode')
    if (hasZone1) {
      headers.push('Z1 Mode')
      headers.push('Z1 SP')
      headers.push('Z1 RA')
      headers.push('Z1 SA')
    }
    if (hasZone2) {
      headers.push('Z2 Mode')
      headers.push('Z2 SP')
      headers.push('Z2 RA')
      headers.push('Z2 SA')
    }
    headers.push('Ambient')
    headers.push('Engine Speed')
    headers.push('Battery Volts')
    headers.push('Location')
    const format = 'dd-MMM-yyyy hh:mm a'
    const body = data.map((sample) => {
      const temp: Record<string, string | number | AddressCell> = {
        Date: DateTime.fromJSDate(sample.sampleTime).toFormat(format),
      }
      temp['Reefer State'] = sample.reeferState ?? ''
      temp['Mode'] = sample.mode ?? ''
      temp['Source'] = sample.source ?? ''
      temp['Operational Mode'] = sample.operationalMode ?? ''
      if (hasZone1) {
        Object.assign(temp, {
          'Z1 Mode': sample.zone1Mode ?? '',
          'Z1 SP': formatReeferTemperature(sample.reeferState, sample.zone1SP, units),
          'Z1 RA': formatReeferTemperature(sample.reeferState, sample.zone1RA, units),
          'Z1 SA': formatReeferTemperature(sample.reeferState, sample.zone1SA, units),
        })
      }
      if (hasZone2) {
        Object.assign(temp, {
          'Z2 Mode': sample.zone2Mode ?? '',
          'Z2 SP': formatReeferTemperature(sample.reeferState, sample.zone2SP, units),
          'Z2 RA': formatReeferTemperature(sample.reeferState, sample.zone2RA, units),
          'Z2 SA': formatReeferTemperature(sample.reeferState, sample.zone2SA, units),
        })
      }
      temp['Ambient'] = formatReeferTemperature(sample.reeferState, sample.ambientTemperature, units)
      temp['Engine Speed'] = sample.engineSpeed ?? ''
      temp['Battery Volts'] = sample.batteryVolts ?? ''
      temp['Location'] = genericLocationDrilldown({
        configuration,
        address: sample.location,
        latitude: sample.latitude,
        longitude: sample.longitude,
        companyId: parameters.companyId,
        markerFields: {
          Asset: sample.assetName,
          Time: sample.sampleTime.toLocaleString(),
        },
      })
      return temp
    })
    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as ReeferOperationHistoryParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
            origin: 'ReeferOperationHistory#caption',
          })
          if (response.data?.length) {
            const asset = response.data[0]
            setAsset(asset)
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferOperationHistory
